import React, { useState, useEffect, useCallback } from 'react';
import { templateService, userService } from '../services/api';
import {
  Star,
  Search,
  Eye,
  Download,
  Edit,
  Trash2,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  FileText,
  Clock,
  CheckCircle,
  User,
  Users,
  AlertTriangle
} from 'lucide-react';
import useModal from '../hooks/useModal';
import useNotification from '../hooks/useNotification';
import Notification from './common/Notification';
import ConfirmDialog from './common/ConfirmDialog';

const TemplateLibrary = () => {
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('library');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPracticeArea, setSelectedPracticeArea] = useState('');
  const [selectedBillingType, setSelectedBillingType] = useState('');
  const [sortBy, setSortBy] = useState('popularity');

  // Library templates
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // User templates
  const [userTemplates, setUserTemplates] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  // Modal states
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Modal behavior for preview
  const { handleBackdropMouseDown: handlePreviewMouseDown, handleBackdropMouseUp: handlePreviewMouseUp } = useModal(!!previewTemplate, () => setPreviewTemplate(null));
  const [importTemplate, setImportTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadLibraryTemplates = useCallback(async () => {
    console.log('loadLibraryTemplates called');
    setLibraryLoading(true);
    try {
      const params = {
        category: selectedCategory,
        practice_area: selectedPracticeArea,
        billing_type: selectedBillingType,
        search: searchTerm,
        sort_by: sortBy
      };
      console.log('Library templates params:', params);
      
      const data = await templateService.getLibraryTemplates(params);
      console.log('Library templates response:', data);
      console.log('Response type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      console.log('Response keys:', Object.keys(data || {}));
      
      // Handle different response formats
      let templates = [];
      if (Array.isArray(data)) {
        templates = data;
      } else if (data && Array.isArray(data.data)) {
        templates = data.data;
      } else if (data && data.templates && Array.isArray(data.templates)) {
        templates = data.templates;
      } else {
        console.warn('Unexpected API response format:', data);
        templates = [];
      }
      
      console.log('Final templates array:', templates);
      setLibraryTemplates(templates);
    } catch (error) {
      console.error('Error loading library templates:', error);
    } finally {
      setLibraryLoading(false);
    }
  }, [selectedCategory, selectedPracticeArea, selectedBillingType, sortBy, searchTerm]);

  const loadUserTemplates = useCallback(async () => {
    setUserLoading(true);
    try {
      const data = await templateService.getCaseTemplates();
      console.log('User templates response:', data); // Debug log
      // The API returns data directly, not wrapped in an object
      const templates = Array.isArray(data) ? data : data.data || [];
      setUserTemplates(templates.filter(t => !t.is_default_library));
    } catch (error) {
      console.error('Error loading user templates:', error);
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Load data
  useEffect(() => {
    console.log('TemplateLibrary useEffect triggered, activeTab:', activeTab);
    if (activeTab === 'library') {
      loadLibraryTemplates();
    } else if (activeTab === 'my-templates') {
      loadUserTemplates();
    }
  }, [activeTab, loadLibraryTemplates, loadUserTemplates]);

  // Handlers
  const handleSearch = () => {
    if (activeTab === 'library') {
      loadLibraryTemplates();
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
  };

  const handleImport = (template) => {
    setImportTemplate(template);
  };

  const handleImportConfirm = async (importData) => {
    try {
      await templateService.importTemplate(importData);
      setImportTemplate(null);
      if (activeTab === 'my-templates') {
        loadUserTemplates();
      }
      showSuccess('Template imported successfully!');
    } catch (error) {
      console.error('Error importing template:', error);
      showError('Failed to import template');
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setActiveTab('my-templates');
    loadUserTemplates();
  };

  const handleEditClick = (template) => {
    setEditingTemplate(template);
  };

  const handleDeleteClick = (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await templateService.deleteCaseTemplate(templateToDelete);
      loadUserTemplates();
      showSuccess('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
    } finally {
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

  const tabs = [
    { id: 'library', label: 'Template Library', icon: Star },
    { id: 'my-templates', label: 'My Templates', icon: Edit },
    { id: 'create', label: 'Create Template', icon: Plus }
  ];
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Template Management</h1>
        <p className="text-gray-600">Browse template library, manage your templates, and create custom case templates</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setShowCreateForm(tab.id === 'create');
                }}
                className={`${
                  isActive
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
      {/* Template Library Tab */}
      {activeTab === 'library' && (
        <div>
          {/* Search and View Toggle */}
          <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search templates, projects, and tasks..."
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent shadow-sm text-slate-700 placeholder-slate-400"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSearch}
                  className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-xl hover:from-yellow-500 hover:to-yellow-600 flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Search className="h-4 w-4" />
                  <span className="font-medium">Search</span>
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Billing Type</label>
                <select
                  value={selectedBillingType}
                  onChange={(e) => setSelectedBillingType(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent shadow-sm text-slate-700"
                >
                  <option value="">All Billing Types</option>
                  <option value="hourly">Hourly</option>
                  <option value="flat_fee">Flat Fee</option>
                  <option value="contingency">Contingency</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent shadow-sm text-slate-700"
                >
                  <option value="popularity">Popularity</option>
                  <option value="rating">Rating</option>
                  <option value="name">Name</option>
                  <option value="created_at">Date Created</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                    setSelectedPracticeArea('');
                    setSelectedBillingType('');
                    setSortBy('popularity');
                    loadLibraryTemplates();
                  }}
                  className="w-full px-4 py-3 bg-white text-slate-600 rounded-lg hover:bg-slate-50 border border-slate-300 flex items-center justify-center space-x-2 transition-all duration-200 hover:shadow-sm font-medium"
                >
                  <X className="h-4 w-4" />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* Hierarchical Template Tree */}
          <HierarchicalTemplateTree
            templates={libraryTemplates}
            loading={libraryLoading}
            onPreview={handlePreview}
            onImport={handleImport}
            searchTerm={searchTerm}
            showSuccess={showSuccess}
          />
        </div>
      )}

      {/* My Templates Tab */}
      {activeTab === 'my-templates' && (
        <div>
          <MyTemplatesGrid
            templates={userTemplates}
            loading={userLoading}
            onRefresh={loadUserTemplates}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        </div>
      )}

      {/* Create Template Tab */}
      {activeTab === 'create' && showCreateForm && (
        <CreateTemplateForm
          onCancel={() => {
            setShowCreateForm(false);
            setActiveTab('library');
          }}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modals */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onImport={() => {
            setPreviewTemplate(null);
            handleImport(previewTemplate);
          }}
          handleBackdropMouseDown={handlePreviewMouseDown}
          handleBackdropMouseUp={handlePreviewMouseUp}
        />
      )}

      {importTemplate && (
        <TemplateImportModal
          template={importTemplate}
          onConfirm={handleImportConfirm}
          onCancel={() => setImportTemplate(null)}
        />
      )}

      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={() => {
            setEditingTemplate(null);
            loadUserTemplates();
            showSuccess('Template updated successfully');
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

// Hierarchical Template Tree Component
const HierarchicalTemplateTree = ({ templates, loading, onPreview, onImport, searchTerm, showSuccess }) => {
  const [expandedPracticeAreas, setExpandedPracticeAreas] = useState(new Set());
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [templateDetails, setTemplateDetails] = useState({});

  // Group templates by practice area
  const groupedTemplates = React.useMemo(() => {
    const groups = {};
    templates.forEach(template => {
      const practiceArea = template.practice_area || 'Other';
      if (!groups[practiceArea]) {
        groups[practiceArea] = [];
      }
      groups[practiceArea].push(template);
    });
    
    // Sort practice areas alphabetically
    return Object.keys(groups)
      .sort()
      .reduce((sorted, key) => {
        sorted[key] = groups[key].sort((a, b) => a.name.localeCompare(b.name));
        return sorted;
      }, {});
  }, [templates]);

  // Load template details (projects and tasks) when expanded
  const loadTemplateDetails = async (templateId) => {
    if (templateDetails[templateId]) return;
    
    try {
      const details = await templateService.getCaseTemplatePreview(templateId);
      setTemplateDetails(prev => ({
        ...prev,
        [templateId]: details
      }));
    } catch (error) {
      console.error('Error loading template details:', error);
      setTemplateDetails(prev => ({
        ...prev,
        [templateId]: { projects: [], error: true }
      }));
    }
  };

  const togglePracticeArea = (practiceArea) => {
    setExpandedPracticeAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(practiceArea)) {
        newSet.delete(practiceArea);
      } else {
        newSet.add(practiceArea);
      }
      return newSet;
    });
  };

  const toggleTemplate = async (templateId) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
        loadTemplateDetails(templateId);
      }
      return newSet;
    });
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleImportSelected = async () => {
    const templatesToImport = Array.from(selectedTemplates).map(templateId => {
      const template = templates.find(t => t.id === templateId);
      return template;
    }).filter(Boolean);

    for (const template of templatesToImport) {
      try {
        await onImport(template);
      } catch (error) {
        console.error(`Error importing template ${template.name}:`, error);
      }
    }

    setSelectedTemplates(new Set());
    showSuccess(`Successfully imported ${templatesToImport.length} template(s)`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading template library...</p>
        </div>
      </div>
    );
  }

  if (Object.keys(groupedTemplates).length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center">
          <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600">Try adjusting your search terms or filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header with bulk actions */}
      {selectedTemplates.size > 0 && (
        <div className="border-b border-slate-200 p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                {selectedTemplates.size}
              </span>
              template{selectedTemplates.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-3">
              <button
                onClick={handleImportSelected}
                className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-lg hover:from-yellow-500 hover:to-yellow-600 text-sm font-medium shadow-sm transition-all duration-200 hover:shadow-md"
              >
                Import Selected
              </button>
              <button
                onClick={() => setSelectedTemplates(new Set())}
                className="px-4 py-2 bg-white text-slate-600 rounded-lg hover:bg-slate-50 border border-slate-300 text-sm font-medium transition-all duration-200 hover:shadow-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-h-[800px] overflow-y-auto">
        {Object.entries(groupedTemplates).map(([practiceArea, practiceTemplates]) => (
          <div key={practiceArea} className="border-b border-slate-100 last:border-b-0">
            {/* Practice Area Header */}
            <div 
              className="flex items-center p-5 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 cursor-pointer select-none transition-all duration-200 group"
              onClick={() => togglePracticeArea(practiceArea)}
            >
              {expandedPracticeAreas.has(practiceArea) ? (
                <ChevronDown className="h-4 w-4 text-slate-500 mr-3 transition-colors group-hover:text-yellow-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-500 mr-3 transition-colors group-hover:text-yellow-600" />
              )}
              {expandedPracticeAreas.has(practiceArea) ? (
                <FolderOpen className="h-6 w-6 text-yellow-600 mr-4 drop-shadow-sm" />
              ) : (
                <Folder className="h-6 w-6 text-gray-600 mr-4 group-hover:text-yellow-600 transition-colors" />
              )}
              <span className="font-bold text-lg text-slate-800 group-hover:text-gray-800 transition-colors">{practiceArea}</span>
              <span className="ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 group-hover:bg-gray-100 group-hover:text-yellow-600 transition-all duration-200">
                {practiceTemplates.length}
              </span>
            </div>

            {/* Practice Area Templates */}
            {expandedPracticeAreas.has(practiceArea) && (
              <div className="pl-6">
                {practiceTemplates.map(template => (
                  <TemplateTreeItem
                    key={template.id}
                    template={template}
                    isExpanded={expandedTemplates.has(template.id)}
                    isSelected={selectedTemplates.has(template.id)}
                    templateDetails={templateDetails[template.id]}
                    expandedProjects={expandedProjects}
                    onToggle={() => toggleTemplate(template.id)}
                    onToggleProject={toggleProject}
                    onToggleSelection={() => toggleTemplateSelection(template.id)}
                    onPreview={() => onPreview(template)}
                    onImport={() => onImport(template)}
                    searchTerm={searchTerm}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Individual Template Tree Item Component
const TemplateTreeItem = ({ 
  template, 
  isExpanded, 
  isSelected, 
  templateDetails,
  expandedProjects,
  onToggle, 
  onToggleProject,
  onToggleSelection,
  onPreview, 
  onImport,
  searchTerm 
}) => {
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">{part}</mark>
      ) : part
    );
  };

  return (
    <div className="border-l-2 border-slate-200 ml-4">
      {/* Template Header */}
      <div className="flex items-center p-4 hover:bg-gradient-to-r hover:from-white hover:to-green-50 group transition-all duration-200 rounded-r-lg">
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex items-center mr-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              className="h-4 w-4 text-yellow-600 rounded border-slate-300 focus:ring-yellow-500 focus:ring-offset-0 focus:ring-2"
            />
            <button
              onClick={onToggle}
              className="ml-3 p-1 hover:bg-slate-200 rounded-full transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
              )}
            </button>
            <div className="relative ml-2 mr-3">
              <FileText className="h-5 w-5 text-emerald-600 drop-shadow-sm" />
              {isSelected && (
                <div className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-yellow-500 rounded-full ring-2 ring-white"></div>
              )}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-800 truncate group-hover:text-emerald-700 transition-colors">
                  {highlightText(template.name, searchTerm)}
                </h4>
                <p className="text-sm text-slate-600 truncate mt-0.5">
                  {highlightText(template.description || 'No description', searchTerm)}
                </p>
                <div className="flex items-center space-x-4 mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                    {template.default_billing_type || 'Not set'}
                  </span>
                  {template.rating_average > 0 && (
                    <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-full">
                      <Star className="h-3 w-3 text-yellow-500 fill-current mr-1" />
                      <span className="text-xs font-medium text-yellow-800">{template.rating_average.toFixed(1)}</span>
                    </div>
                  )}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-yellow-600">
                    {template.import_count || 0} uses
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Template Actions */}
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={onPreview}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
            title="Preview"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={onImport}
            className="p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            title="Import"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Template Details (Projects and Tasks) */}
      {isExpanded && (
        <div className="pl-8 border-l border-gray-200 ml-4">
          {templateDetails ? (
            templateDetails.error ? (
              <div className="p-3 text-sm text-red-600">
                Failed to load template details
              </div>
            ) : templateDetails.projects && templateDetails.projects.length > 0 ? (
              templateDetails.projects.map(project => (
                <ProjectTreeItem
                  key={project.id}
                  project={project}
                  isExpanded={expandedProjects.has(project.id)}
                  onToggle={() => onToggleProject(project.id)}
                  searchTerm={searchTerm}
                />
              ))
            ) : (
              <div className="p-3 text-sm text-gray-500">
                No projects defined for this template
              </div>
            )
          ) : (
            <div className="p-3 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400 mr-2"></div>
              Loading projects...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Project Tree Item Component  
const ProjectTreeItem = ({ project, isExpanded, onToggle, searchTerm }) => {
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">{part}</mark>
      ) : part
    );
  };

  return (
    <div className="border-l-2 border-yellow-200 ml-6 bg-gradient-to-r from-yellow-50/30 to-transparent rounded-r-lg">
      {/* Project Header */}
      <div className="flex items-center p-3 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-yellow-50 group transition-all duration-200 rounded-r-lg">
        <button
          onClick={onToggle}
          className="p-1 hover:bg-yellow-200 rounded-full mr-3 transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-yellow-600" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-yellow-600" />
          )}
        </button>
        
        <div className="relative mr-3">
          {isExpanded ? (
            <FolderOpen className="h-5 w-5 text-yellow-600 drop-shadow-sm" />
          ) : (
            <Folder className="h-5 w-5 text-yellow-600 group-hover:text-yellow-600 transition-colors" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h5 className="text-sm font-semibold text-slate-800 truncate group-hover:text-yellow-700 transition-colors">
            {highlightText(project.name, searchTerm)}
          </h5>
          <div className="flex items-center space-x-3 mt-1">
            {project.estimated_hours && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                <Clock className="h-3 w-3 mr-1" />
                {project.estimated_hours}h
              </span>
            )}
            {project.estimated_value && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ${project.estimated_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Project Tasks */}
      {isExpanded && project.tasks && project.tasks.length > 0 && (
        <div className="pl-8 border-l-2 border-slate-200 ml-4 bg-gradient-to-r from-slate-50/40 to-transparent rounded-r-lg">
          {project.tasks.map((task, index) => (
            <div key={task.id} className={`flex items-center p-3 hover:bg-gradient-to-r hover:from-slate-50 hover:to-purple-50 group transition-all duration-200 rounded-r-lg ${index === project.tasks.length - 1 ? 'mb-2' : ''}`}>
              <div className="relative mr-3">
                <CheckCircle className="h-4 w-4 text-slate-400 group-hover:text-purple-500 transition-colors" />
                {task.court_deadline && (
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full ring-1 ring-white"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700 transition-colors">
                  {highlightText(task.title || task.name, searchTerm)}
                </span>
                <div className="flex items-center space-x-2 mt-1">
                  {task.estimated_hours && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                      {task.estimated_hours}h
                    </span>
                  )}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 capitalize">
                    {task.task_type}
                  </span>
                  {task.court_deadline && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800">
                      Court Deadline
                    </span>
                  )}
                  {task.billable && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      Billable
                    </span>
                  )}
                  {task.default_assigned_to_user_name && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      <User className="h-3 w-3 mr-0.5" />
                      {task.default_assigned_to_user_name}
                    </span>
                  )}
                  {task.default_assigned_to_user_id && !task.default_assigned_to_user_name && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800">
                      <AlertTriangle className="h-3 w-3 mr-0.5" />
                      Assignee departed
                    </span>
                  )}
                  {task.allowed_roles && task.allowed_roles.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                      <Users className="h-3 w-3 mr-0.5" />
                      {task.allowed_roles.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// My Templates Grid Component
const MyTemplatesGrid = ({ templates, loading, onRefresh, onEdit, onDelete }) => {

  if (loading) {
    return <div className="text-center py-8">Loading templates...</div>;
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-12">
        <Edit className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
        <p className="text-gray-600">Import templates from the library or create your own custom templates.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {(templates || []).map((template) => (
        <MyTemplateCard
          key={template.id}
          template={template}
          onEdit={() => onEdit(template)}
          onDelete={() => onDelete(template.id)}
        />
      ))}
    </div>
  );
};

// My Template Card Component
const MyTemplateCard = ({ template, onEdit, onDelete }) => {

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
          <p className="text-sm text-gray-600 mb-3">{template.description}</p>
        </div>
        <div className="flex space-x-1">
          <button
            onClick={() => onEdit(template)}
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Practice Area:</span>
          <span>{template.practice_area || 'General'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Scope:</span>
          <span className="capitalize">{template.scope}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Created:</span>
          <span>{new Date(template.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};
// Modal Components
const TemplatePreviewModal = ({ template, onClose, onImport, handleBackdropMouseDown, handleBackdropMouseUp }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{template.name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900">Description</h3>
              <p className="text-gray-600">{template.description || 'No description available'}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-gray-900">Practice Area</h3>
                <p className="text-gray-600">{template.practice_area || 'General'}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Billing Type</h3>
                <p className="text-gray-600 capitalize">{template.default_billing_type || 'Not specified'}</p>
              </div>
            </div>

            {template.tags && template.tags.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {template.tags.map((tag, index) => (
                    <span key={index} className="bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3 mt-6">
            <button
              onClick={onImport}
              className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
            >
              Import Template
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
const TemplateImportModal = ({ template, onConfirm, onCancel }) => {
  const [importData, setImportData] = useState({
    template_id: template.id,
    scope: 'firm',
    name: template.name,
    description: template.description
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(importData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Import Template</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name
              </label>
              <input
                type="text"
                value={importData.name}
                onChange={(e) => setImportData(prev => ({...prev, name: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scope
              </label>
              <select
                value={importData.scope}
                onChange={(e) => setImportData(prev => ({...prev, scope: e.target.value}))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500"
              >
                <option value="firm">Firm-wide</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={importData.description}
                onChange={(e) => setImportData(prev => ({...prev, description: e.target.value}))}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500"
              />
            </div>
          </div>
          
          <div className="flex space-x-3 mt-6">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
            >
              Import
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Inline Task Template Form (reused inside project template forms)
const InlineTaskForm = ({ task, index, onChange, onRemove, onReorder, totalTasks }) => {
  const taskTypes = [
    { value: 'research', label: 'Research' },
    { value: 'drafting', label: 'Drafting' },
    { value: 'filing', label: 'Filing' },
    { value: 'client_communication', label: 'Client Communication' },
    { value: 'court_appearance', label: 'Court Appearance' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'review', label: 'Review' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'other', label: 'Other' }
  ];

  const AVAILABLE_ROLES = ['Attorney', 'Paralegal', 'Admin', 'Staff'];

  const handleField = (field, value) => {
    onChange(index, { ...task, [field]: value });
  };

  const toggleRole = (role) => {
    const currentRoles = task.allowed_roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    handleField('allowed_roles', newRoles.length > 0 ? newRoles : null);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 uppercase">Task {index + 1}</span>
        <div className="flex items-center space-x-1">
          {index > 0 && (
            <button type="button" onClick={() => onReorder(index, index - 1)} className="text-gray-400 hover:text-gray-600 px-1">↑</button>
          )}
          {index < totalTasks - 1 && (
            <button type="button" onClick={() => onReorder(index, index + 1)} className="text-gray-400 hover:text-gray-600 px-1">↓</button>
          )}
          <button type="button" onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600 px-1 ml-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Task Name *</label>
          <input
            type="text"
            value={task.name}
            onChange={(e) => handleField('name', e.target.value)}
            required
            placeholder="e.g., Draft initial petition"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Task Type</label>
          <select
            value={task.task_type}
            onChange={(e) => handleField('task_type', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
          >
            {taskTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <input
          type="text"
          value={task.description || ''}
          onChange={(e) => handleField('description', e.target.value)}
          placeholder="Brief description of the task..."
          className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={task.priority}
            onChange={(e) => handleField('priority', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Est. Hours</label>
          <input
            type="number"
            value={task.estimated_hours || ''}
            onChange={(e) => handleField('estimated_hours', e.target.value ? parseFloat(e.target.value) : null)}
            min="0" step="0.5" placeholder="0"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Due (days)</label>
          <input
            type="number"
            value={task.due_days_offset}
            onChange={(e) => handleField('due_days_offset', parseInt(e.target.value) || 0)}
            min="0" placeholder="0"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500"
          />
        </div>
        <div className="flex items-end space-x-3">
          <label className="flex items-center text-xs text-gray-700">
            <input type="checkbox" checked={task.billable} onChange={(e) => handleField('billable', e.target.checked)}
              className="mr-1 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
            Billable
          </label>
          <label className="flex items-center text-xs text-gray-700">
            <input type="checkbox" checked={task.court_deadline} onChange={(e) => handleField('court_deadline', e.target.checked)}
              className="mr-1 rounded border-gray-300 text-red-600 focus:ring-red-500" />
            Court
          </label>
        </div>
      </div>

      {/* Assignment */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          <Users className="h-3 w-3 inline mr-1" />
          Allowed Roles
        </label>
        <div className="flex flex-wrap gap-1">
          {AVAILABLE_ROLES.map(role => {
            const isChecked = (task.allowed_roles || []).includes(role);
            return (
              <button
                key={role} type="button" onClick={() => toggleRole(role)}
                className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                  isChecked ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-500 hover:border-indigo-300'
                }`}
              >
                {role}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Create Project Template Form (standalone or inline)
const CreateProjectTemplateForm = ({ scope, onCancel, onSuccess, inline = false }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const addTask = () => {
    setTasks(prev => [...prev, {
      name: '', description: '', task_type: 'other', priority: 'medium',
      estimated_hours: null, billable: true, court_deadline: false,
      requires_approval: false, allowed_roles: null, due_days_offset: 0,
      order_index: prev.length
    }]);
  };

  const updateTask = (index, updated) => {
    setTasks(prev => prev.map((t, i) => i === index ? { ...updated, order_index: i } : t));
  };

  const removeTask = (index) => {
    setTasks(prev => prev.filter((_, i) => i !== index).map((t, i) => ({ ...t, order_index: i })));
  };

  const reorderTask = (fromIndex, toIndex) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const [moved] = newTasks.splice(fromIndex, 1);
      newTasks.splice(toIndex, 0, moved);
      return newTasks.map((t, i) => ({ ...t, order_index: i }));
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate all tasks have names
    const invalidTasks = tasks.filter(t => !t.name.trim());
    if (invalidTasks.length > 0) {
      setError('All tasks must have a name');
      setLoading(false);
      return;
    }

    try {
      const data = {
        name,
        description: description || null,
        scope: scope || 'firm',
        estimated_hours: estimatedHours ? parseInt(estimatedHours) : null,
        estimated_value: null,
        default_status: 'ready',
        tags: [],
        task_templates: tasks
      };

      const result = await templateService.createProjectTemplate(data);
      onSuccess(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project template');
    } finally {
      setLoading(false);
    }
  };

  const containerClass = inline
    ? "border border-dashed border-yellow-400 rounded-lg p-4 bg-yellow-50"
    : "bg-white rounded-lg border border-gray-200 p-6";

  return (
    <div className={containerClass}>
      <div className="mb-4">
        <h3 className={`font-bold text-gray-900 ${inline ? 'text-base' : 'text-xl'}`}>
          {inline ? 'New Project Template' : 'Create Project Template'}
        </h3>
        {!inline && <p className="text-gray-600 text-sm mt-1">Create a project template with tasks that can be added to case templates</p>}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="e.g., Discovery Phase"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
            <input
              type="number" value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)}
              min="0" placeholder="0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this project phase..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
          />
        </div>

        {/* Tasks */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-800">
              Tasks {tasks.length > 0 && <span className="text-gray-500 font-normal">({tasks.length})</span>}
            </h4>
            <button
              type="button" onClick={addTask}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-3 w-3 mr-1" /> Add Task
            </button>
          </div>

          {tasks.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg">
              <CheckCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No tasks yet. Add tasks that should be created with this project.</p>
              <button type="button" onClick={addTask}
                className="mt-2 text-sm text-yellow-600 hover:text-yellow-700 font-medium">
                Add your first task
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task, index) => (
                <InlineTaskForm
                  key={index} task={task} index={index}
                  onChange={updateTask} onRemove={removeTask}
                  onReorder={reorderTask} totalTasks={tasks.length}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex space-x-3 pt-4">
          <button type="submit" disabled={loading || !name.trim()}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
            {loading ? 'Creating...' : inline ? 'Create Project' : 'Create Project Template'}
          </button>
          <button type="button" onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Create Template Form - now with template type selector
const CreateTemplateForm = ({ onCancel, onSuccess }) => {
  const [templateType, setTemplateType] = useState('case');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    practice_area: '',
    scope: 'firm',
    default_billing_type: '',
    default_hourly_rate: '',
    default_estimated_hours: '',
    default_flat_fee_amount: '',
    default_contingency_percentage: '',
    tags: [],
    project_templates: []
  });

  // Available project templates
  const [availableProjectTemplates, setAvailableProjectTemplates] = useState([]);
  const [projectTemplatesLoading, setProjectTemplatesLoading] = useState(false);
  const [showInlineProjectForm, setShowInlineProjectForm] = useState(false);

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadProjectTemplates = useCallback(async () => {
    setProjectTemplatesLoading(true);
    try {
      const data = await templateService.getProjectTemplates();
      setAvailableProjectTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading project templates:', err);
      setAvailableProjectTemplates([]);
    } finally {
      setProjectTemplatesLoading(false);
    }
  }, []);

  // Load available project templates
  useEffect(() => {
    if (templateType === 'case') {
      loadProjectTemplates();
    }
  }, [templateType, loadProjectTemplates]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  // Project template handlers
  const handleAddProjectTemplate = (projectTemplate) => {
    const isAlreadyAdded = formData.project_templates.some(
      pt => pt.project_template_id === projectTemplate.id
    );
    if (!isAlreadyAdded) {
      setFormData(prev => ({
        ...prev,
        project_templates: [...prev.project_templates, {
          project_template_id: projectTemplate.id,
          sort_order: prev.project_templates.length,
          custom_name: null, custom_estimated_hours: null, custom_estimated_value: null,
          _template: projectTemplate
        }]
      }));
    }
  };

  const handleRemoveProjectTemplate = (projectTemplateId) => {
    setFormData(prev => ({
      ...prev,
      project_templates: prev.project_templates
        .filter(pt => pt.project_template_id !== projectTemplateId)
        .map((pt, index) => ({ ...pt, sort_order: index }))
    }));
  };

  const handleUpdateProjectTemplate = (projectTemplateId, field, value) => {
    setFormData(prev => ({
      ...prev,
      project_templates: prev.project_templates.map(pt =>
        pt.project_template_id === projectTemplateId ? { ...pt, [field]: value || null } : pt
      )
    }));
  };

  const handleReorderProjectTemplate = (fromIndex, toIndex) => {
    setFormData(prev => {
      const newPts = [...prev.project_templates];
      const [moved] = newPts.splice(fromIndex, 1);
      newPts.splice(toIndex, 0, moved);
      return { ...prev, project_templates: newPts.map((pt, i) => ({ ...pt, sort_order: i })) };
    });
  };

  const handleInlineProjectCreated = (newProject) => {
    setShowInlineProjectForm(false);
    // Reload project templates so it shows up in the available list
    loadProjectTemplates();
    // Auto-add the new project to selected list
    handleAddProjectTemplate(newProject);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        default_hourly_rate: formData.default_hourly_rate ? parseFloat(formData.default_hourly_rate) : null,
        default_estimated_hours: formData.default_estimated_hours ? parseInt(formData.default_estimated_hours) : null,
        default_flat_fee_amount: formData.default_flat_fee_amount ? parseFloat(formData.default_flat_fee_amount) : null,
        default_contingency_percentage: formData.default_contingency_percentage ? parseFloat(formData.default_contingency_percentage) : null,
        project_templates: formData.project_templates.map(({ _template, ...pt }) => pt)
      };

      await templateService.createCaseTemplate(submitData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const billingTypes = [
    { value: '', label: 'Select billing type' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'flat_fee', label: 'Flat Fee' },
    { value: 'contingency', label: 'Contingency' },
    { value: 'retainer', label: 'Retainer' }
  ];

  // ---- Project Template creation mode ----
  if (templateType === 'project') {
    return (
      <div className="space-y-4">
        {/* Type selector */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">What do you want to create?</label>
          <div className="flex space-x-3">
            <button type="button" onClick={() => setTemplateType('case')}
              className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 text-gray-600 hover:border-yellow-300 text-sm font-medium text-center">
              <Folder className="h-5 w-5 mx-auto mb-1 text-gray-400" />
              Case Template
            </button>
            <button type="button" onClick={() => setTemplateType('project')}
              className="flex-1 px-4 py-3 rounded-lg border-2 border-yellow-500 bg-yellow-50 text-yellow-700 text-sm font-medium text-center">
              <FileText className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
              Project Template
            </button>
          </div>
        </div>

        <CreateProjectTemplateForm
          scope="firm"
          onCancel={onCancel}
          onSuccess={() => onSuccess()}
        />
      </div>
    );
  }

  // ---- Case Template creation mode ----
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Type selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">What do you want to create?</label>
        <div className="flex space-x-3 mb-4">
          <button type="button" onClick={() => setTemplateType('case')}
            className="flex-1 px-4 py-3 rounded-lg border-2 border-yellow-500 bg-yellow-50 text-yellow-700 text-sm font-medium text-center">
            <Folder className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
            Case Template
          </button>
          <button type="button" onClick={() => setTemplateType('project')}
            className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 text-gray-600 hover:border-yellow-300 text-sm font-medium text-center">
            <FileText className="h-5 w-5 mx-auto mb-1 text-gray-400" />
            Project Template
          </button>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Create Case Template</h2>
        <p className="text-gray-600 text-sm">Build a case template with projects and tasks for your practice</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="e.g., Custom Divorce Template" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Practice Area</label>
            <input type="text" name="practice_area" value={formData.practice_area} onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="e.g., Family Law" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
            placeholder="Describe when and how this template should be used..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Template Scope</label>
          <select name="scope" value={formData.scope} onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500">
            <option value="firm">Firm-wide (visible to all firm members)</option>
            <option value="personal">Personal (only visible to you)</option>
          </select>
        </div>

        {/* Billing Configuration */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Default Billing Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Type</label>
              <select name="default_billing_type" value={formData.default_billing_type} onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500">
                {billingTypes.map(type => (<option key={type.value} value={type.value}>{type.label}</option>))}
              </select>
            </div>
            {formData.default_billing_type === 'hourly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Hourly Rate ($)</label>
                <input type="number" name="default_hourly_rate" value={formData.default_hourly_rate}
                  onChange={handleInputChange} min="0" step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
            )}
            {formData.default_billing_type === 'hourly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Hours</label>
                <input type="number" name="default_estimated_hours" value={formData.default_estimated_hours}
                  onChange={handleInputChange} min="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
            )}
            {formData.default_billing_type === 'flat_fee' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Flat Fee Amount ($)</label>
                <input type="number" name="default_flat_fee_amount" value={formData.default_flat_fee_amount}
                  onChange={handleInputChange} min="0" step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
            )}
            {formData.default_billing_type === 'contingency' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contingency Percentage (%)</label>
                <input type="number" name="default_contingency_percentage" value={formData.default_contingency_percentage}
                  onChange={handleInputChange} min="0" max="100" step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500" />
              </div>
            )}
          </div>
        </div>

        {/* Project Templates */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Project Templates</h3>
              <p className="text-sm text-gray-600">Add project templates to include when creating cases from this template.</p>
            </div>
            {!showInlineProjectForm && (
              <button type="button" onClick={() => setShowInlineProjectForm(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium bg-yellow-500 text-white rounded-md hover:bg-yellow-600">
                <Plus className="h-4 w-4 mr-1" /> New Project
              </button>
            )}
          </div>

          {/* Inline Project Creation */}
          {showInlineProjectForm && (
            <div className="mb-6">
              <CreateProjectTemplateForm
                scope={formData.scope}
                inline={true}
                onCancel={() => setShowInlineProjectForm(false)}
                onSuccess={handleInlineProjectCreated}
              />
            </div>
          )}

          {projectTemplatesLoading ? (
            <div className="text-center py-4">Loading project templates...</div>
          ) : (
            <>
              {/* Available Project Templates */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Available Project Templates</h4>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                  {availableProjectTemplates.length === 0 ? (
                    <div className="p-6 text-center">
                      <Folder className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-2">No project templates available yet.</p>
                      {!showInlineProjectForm && (
                        <button type="button" onClick={() => setShowInlineProjectForm(true)}
                          className="text-sm text-yellow-600 hover:text-yellow-700 font-medium">
                          Create your first project template
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {availableProjectTemplates.map((template) => {
                        const isAdded = formData.project_templates.some(pt => pt.project_template_id === template.id);
                        return (
                          <div key={template.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{template.name}</div>
                              {template.description && <div className="text-xs text-gray-500 mt-1">{template.description}</div>}
                              <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                                {template.estimated_hours && <span>{template.estimated_hours}h</span>}
                                {template.estimated_value && <span>${template.estimated_value}</span>}
                                <span className="capitalize">{template.scope}</span>
                              </div>
                            </div>
                            <button type="button" onClick={() => handleAddProjectTemplate(template)} disabled={isAdded}
                              className={`px-3 py-1 text-xs font-medium rounded-md ${
                                isAdded ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-yellow-600 hover:bg-blue-200'
                              }`}>
                              {isAdded ? 'Added' : 'Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Project Templates */}
              {formData.project_templates.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Selected Project Templates</h4>
                  <div className="space-y-3">
                    {formData.project_templates.map((projectTemplate, index) => (
                      <div key={projectTemplate.project_template_id} className="border border-gray-200 rounded-md p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{projectTemplate._template.name}</div>
                            <div className="text-xs text-gray-500 mt-1">Order: {index + 1}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {index > 0 && (
                              <button type="button" onClick={() => handleReorderProjectTemplate(index, index - 1)}
                                className="text-gray-400 hover:text-gray-600" title="Move up">↑</button>
                            )}
                            {index < formData.project_templates.length - 1 && (
                              <button type="button" onClick={() => handleReorderProjectTemplate(index, index + 1)}
                                className="text-gray-400 hover:text-gray-600" title="Move down">↓</button>
                            )}
                            <button type="button" onClick={() => handleRemoveProjectTemplate(projectTemplate.project_template_id)}
                              className="text-red-400 hover:text-red-600" title="Remove">×</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Custom Name (optional)</label>
                            <input type="text" value={projectTemplate.custom_name || ''}
                              onChange={(e) => handleUpdateProjectTemplate(projectTemplate.project_template_id, 'custom_name', e.target.value)}
                              placeholder={projectTemplate._template.name}
                              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-yellow-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Custom Hours (optional)</label>
                            <input type="number" value={projectTemplate.custom_estimated_hours || ''}
                              onChange={(e) => handleUpdateProjectTemplate(projectTemplate.project_template_id, 'custom_estimated_hours', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder={projectTemplate._template.estimated_hours?.toString() || ''} min="0"
                              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-yellow-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Custom Value (optional)</label>
                            <input type="number" value={projectTemplate.custom_estimated_value || ''}
                              onChange={(e) => handleUpdateProjectTemplate(projectTemplate.project_template_id, 'custom_estimated_value', e.target.value ? parseFloat(e.target.value) : null)}
                              placeholder={projectTemplate._template.estimated_value?.toString() || ''} min="0" step="0.01"
                              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-yellow-500" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Tags */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
          <div className="flex items-center space-x-2 mb-3">
            <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add a tag..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              onKeyPress={(e) => { if (e.key === 'Enter') handleAddTag(e); }} />
            <button type="button" onClick={handleAddTag}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600">Add</button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 text-yellow-600 hover:text-gray-800">×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-6">
          <button type="submit" disabled={loading || !formData.name}
            className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Creating...' : 'Create Case Template'}
          </button>
          <button type="button" onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
        </div>
      </form>
    </div>
  );
};

// Edit Template Modal - allows editing task assignments within a case template
const EditTemplateModal = ({ template, onClose, onSave }) => {
  const [templateDetails, setTemplateDetails] = useState(null);
  const [firmUsers, setFirmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  const AVAILABLE_ROLES = ['Attorney', 'Paralegal', 'Admin', 'Staff'];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [details, users] = await Promise.all([
          templateService.getCaseTemplatePreview(template.id),
          userService.getUsers(false)
        ]);
        setTemplateDetails(details);
        const userList = Array.isArray(users) ? users : users.data || [];
        setFirmUsers(userList.filter(u => u.is_active !== false));
        // Auto-expand all projects
        if (details.projects) {
          setExpandedProjects(new Set(details.projects.map(p => p.id)));
        }
      } catch (error) {
        console.error('Error loading template details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [template.id]);

  const handleAssignmentChange = async (task, type, value) => {
    if (!task.ptt_id) return;

    setSaving(prev => ({ ...prev, [task.ptt_id]: true }));
    try {
      const assignment = {};
      if (type === 'user') {
        assignment.assigned_to_user_id = value || null;
        // Clear roles when assigning a specific user
        if (value) {
          assignment.allowed_roles = null;
        }
      } else if (type === 'roles') {
        assignment.allowed_roles = value.length > 0 ? value : null;
        // Clear specific user when setting roles
        if (value.length > 0) {
          assignment.assigned_to_user_id = null;
        }
      }

      await templateService.updateTaskTemplateAssignment(task.ptt_id, assignment);

      // Update local state
      setTemplateDetails(prev => ({
        ...prev,
        projects: prev.projects.map(p => ({
          ...p,
          tasks: p.tasks.map(t => {
            if (t.ptt_id !== task.ptt_id) return t;
            if (type === 'user') {
              const user = firmUsers.find(u => u.id === value);
              return {
                ...t,
                default_assigned_to_user_id: value || null,
                default_assigned_to_user_name: user ? `${user.first_name} ${user.last_name}` : null,
                allowed_roles: value ? null : t.allowed_roles
              };
            }
            return {
              ...t,
              allowed_roles: value.length > 0 ? value : null,
              default_assigned_to_user_id: value.length > 0 ? null : t.default_assigned_to_user_id,
              default_assigned_to_user_name: value.length > 0 ? null : t.default_assigned_to_user_name
            };
          })
        }))
      }));
    } catch (error) {
      console.error('Error updating assignment:', error);
    } finally {
      setSaving(prev => ({ ...prev, [task.ptt_id]: false }));
    }
  };

  const handleRoleToggle = (task, role) => {
    const currentRoles = task.allowed_roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    handleAssignmentChange(task, 'roles', newRoles);
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Template: {template.name}</h2>
            <p className="text-sm text-gray-600 mt-1">Set default assignees or allowed roles for each task</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading template details...</p>
            </div>
          ) : templateDetails && templateDetails.projects ? (
            <div className="space-y-4">
              {templateDetails.projects.map(project => (
                <div key={project.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Project Header */}
                  <div
                    className="flex items-center p-4 bg-gradient-to-r from-yellow-50 to-amber-50 cursor-pointer hover:from-yellow-100 hover:to-amber-100 transition-colors"
                    onClick={() => toggleProject(project.id)}
                  >
                    {expandedProjects.has(project.id) ? (
                      <ChevronDown className="h-4 w-4 text-yellow-600 mr-3" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-yellow-600 mr-3" />
                    )}
                    <FolderOpen className="h-5 w-5 text-yellow-600 mr-3" />
                    <span className="font-semibold text-gray-800">{project.name}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      {project.tasks?.length || 0} task{project.tasks?.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Task List */}
                  {expandedProjects.has(project.id) && project.tasks && project.tasks.length > 0 && (
                    <div className="divide-y divide-gray-100">
                      {project.tasks.map(task => (
                        <div key={task.ptt_id || task.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            {/* Task Info */}
                            <div className="flex-1 min-w-0 mr-4">
                              <div className="flex items-center">
                                <CheckCircle className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                <span className="font-medium text-gray-800 text-sm">{task.name}</span>
                                {task.court_deadline && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800">
                                    Court Deadline
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 mt-1 ml-6">
                                <span className="text-xs text-gray-500 capitalize">{task.task_type}</span>
                                {task.estimated_hours && (
                                  <span className="text-xs text-gray-500">{task.estimated_hours}h</span>
                                )}
                              </div>
                              {/* Departed user warning */}
                              {task.default_assigned_to_user_id && !task.default_assigned_to_user_name && (
                                <div className="flex items-center mt-2 ml-6 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                  <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                                  Default assignee is no longer active at this firm
                                </div>
                              )}
                            </div>

                            {/* Assignment Controls */}
                            <div className="flex-shrink-0 w-64">
                              <div className="space-y-2">
                                {/* Specific User Dropdown */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <User className="h-3 w-3 inline mr-1" />
                                    Assign to User
                                  </label>
                                  <select
                                    value={task.default_assigned_to_user_id || ''}
                                    onChange={(e) => handleAssignmentChange(task, 'user', e.target.value)}
                                    disabled={saving[task.ptt_id]}
                                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-yellow-500 disabled:opacity-50"
                                  >
                                    <option value="">No specific user</option>
                                    {firmUsers.map(user => (
                                      <option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Role Checkboxes (only show if no specific user) */}
                                {!task.default_assigned_to_user_id && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      <Users className="h-3 w-3 inline mr-1" />
                                      Allowed Roles
                                    </label>
                                    <div className="flex flex-wrap gap-1">
                                      {AVAILABLE_ROLES.map(role => {
                                        const isChecked = (task.allowed_roles || []).includes(role);
                                        return (
                                          <button
                                            key={role}
                                            type="button"
                                            onClick={() => handleRoleToggle(task, role)}
                                            disabled={saving[task.ptt_id]}
                                            className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                              isChecked
                                                ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                                : 'bg-white border-gray-300 text-gray-500 hover:border-indigo-300'
                                            } disabled:opacity-50`}
                                          >
                                            {role}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No projects found in this template
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;
